/* global RedactorX */

import React, { useEffect, useRef, useState } from "react";
import { useField } from "react-final-form";
import "../../../../../src/static/assets/redactor/redactorx.min.css";
import "../../../../../src/static/assets/redactor/redactorx.min.js";
import {
  PrivacyCenterEmbeddableAssetType,
  getEmbeddableAssetHtml,
} from "../../../../../src/js/polaris/util/privacyCenterUtil";
import "./WYSIWYGEditorStyles.css";

interface WYSIWYGEditorProps {
  onChange?: (contents: string) => void;
  onBlur?: (event: FocusEvent, contents: string) => void;
  defaultValue?: string;
  value?: string;
  minHeight?: string;
  maxHeight?: string;
  rows?: number;
  toolbar?: boolean;
  disable?: boolean;
  name?: string;
  id?: string;
  placeholder?: string;
  enablePrivacyCenterToolbar?: boolean;
  availablePrivacyCenterEmbeddedAssets?: Map<string, boolean>;
}

export const WYSIWYGEditor = ({
  onChange,
  onBlur,
  defaultValue,
  value,
  minHeight,
  maxHeight,
  rows,
  toolbar = true,
  name,
  id,
  disable,
  placeholder,
  enablePrivacyCenterToolbar,
  availablePrivacyCenterEmbeddedAssets,
}: WYSIWYGEditorProps) => {
  if (rows) {
    minHeight = `${rows * 40}px`;
  }
  const editorEl = useRef<HTMLTextAreaElement>(null);
  const [red, setRedactor] = useState<any>(undefined);
  useEffect(() => {
    if (editorEl.current) {
      const toolbarContainer = document.querySelector(
        ".rx-toolbar-container.is-rx-toolbar.rx-toolbar-sticky",
      ) as HTMLElement;
      if (toolbarContainer) {
        toolbarContainer.style.zIndex = "auto"; // Set to 'auto' or any specific value you need
      }

      const borderContainer = document.querySelector(
        ".rx-container.rx-container-0.rx-in-blur",
      ) as HTMLElement;
      if (borderContainer) {
        borderContainer.classList.add("border-override");
      }

      if (enablePrivacyCenterToolbar === true) {
        // @ts-ignore: RedactorX is a global variable
        RedactorX.add("plugin", "truevault-privacy-center-toolbar", {
          start: function () {
            this.app.toolbar.add("privacy-center-button", {
              title: "Insert Privacy Center Asset",
              icon: `<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.25 12.8125H12.0625M12.0625 12.8125H14.875M12.0625 12.8125V10M12.0625 12.8125V15.625M3 7.5H4.875C5.91053 7.5 6.75 6.66053 6.75 5.625V3.75C6.75 2.71447 5.91053 1.875 4.875 1.875H3C1.96447 1.875 1.125 2.71447 1.125 3.75V5.625C1.125 6.66053 1.96447 7.5 3 7.5ZM3 15.625H4.875C5.91053 15.625 6.75 14.7855 6.75 13.75V11.875C6.75 10.8395 5.91053 10 4.875 10H3C1.96447 10 1.125 10.8395 1.125 11.875V13.75C1.125 14.7855 1.96447 15.625 3 15.625ZM11.125 7.5H13C14.0355 7.5 14.875 6.66053 14.875 5.625V3.75C14.875 2.71447 14.0355 1.875 13 1.875H11.125C10.0895 1.875 9.25 2.71447 9.25 3.75V5.625C9.25 6.66053 10.0895 7.5 11.125 7.5Z" stroke="#1C1B1F" stroke-width="2" stroke-linecap="square" stroke-linejoin="round" fill="none"/>
              </svg>
              `,
              command: "truevault-privacy-center-toolbar.popup",
            });
          },
          popup: function (params, button) {
            const items = {};
            const commandName = "truevault-privacy-center-toolbar.toggle";

            let currentItemIdx = 1;

            if (availablePrivacyCenterEmbeddedAssets?.get("LIST_OF_DATA_COLLECTED") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "List of Data Collected",
                command: commandName,
                params: { embeddableAssetType: "LIST_OF_DATA_COLLECTED" },
              };
              currentItemIdx++;
            }

            if (availablePrivacyCenterEmbeddedAssets?.get("DATA_DISCLOSURE_TABLE") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "Data Disclosure Table",
                command: commandName,
                params: { embeddableAssetType: "DATA_DISCLOSURE_TABLE" },
              };
              currentItemIdx++;
            }

            if (availablePrivacyCenterEmbeddedAssets?.get("LIST_OF_DATA_SOURCES") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "List of Data Sources",
                command: commandName,
                params: { embeddableAssetType: "LIST_OF_DATA_SOURCES" },
              };
              currentItemIdx++;
            }

            if (availablePrivacyCenterEmbeddedAssets?.get("LIST_OF_DATA_USES") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "List of Data Uses",
                command: commandName,
                params: { embeddableAssetType: "LIST_OF_DATA_USES" },
              };
              currentItemIdx++;
            }

            if (availablePrivacyCenterEmbeddedAssets?.get("DATA_RETENTION_POLICY") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "Data Retention Policy",
                command: commandName,
                params: { embeddableAssetType: "DATA_RETENTION_POLICY" },
              };
              currentItemIdx++;
            }

            if (availablePrivacyCenterEmbeddedAssets?.get("WEB_COOKIES_TABLE") !== false) {
              items[`item${currentItemIdx}`] = {
                title: "Web Cookies Table",
                command: commandName,
                params: { embeddableAssetType: "WEB_COOKIES_TABLE" },
              };
              currentItemIdx++;
            }

            this.app.popup.create("truevault-privacy-center-toolbar", { items });

            this.app.popup.open({ button });
          },
          toggle: function (params: { embeddableAssetType: PrivacyCenterEmbeddableAssetType }) {
            setTimeout(() => {
              this.app.insertion.insertContent({
                html: getEmbeddableAssetHtml(params.embeddableAssetType),
                caret: "after",
              });
            }, 400);

            this.app.popup.close();
          },
        });
      }
      // @ts-ignore: RedactorX is a global variable
      const redactor = RedactorX(editorEl.current, {
        content: value || defaultValue,
        toolbar: toolbar ? {} : false,
        context: true,
        editor: {
          minHeight: minHeight,
          maxHeight: maxHeight,
        },
        link: {
          target: "_blank",
        },
        buttons: {
          context: ["link"],
        },
        subscribe: {
          "editor.change": function (e) {
            if (onChange) {
              onChange(e.params.html);
            }
          },
        },
        plugins: enablePrivacyCenterToolbar ? ["truevault-privacy-center-toolbar"] : [],
        placeholder,
      });
      setRedactor(redactor);
    }
  }, [onChange, defaultValue, onBlur, minHeight, maxHeight, placeholder, toolbar, value]);

  useEffect(() => {
    if (
      value !== undefined &&
      red !== undefined &&
      red.editor &&
      red.editor.getContent() != value
    ) {
      try {
        red.editor.setContent({ html: value });
      } catch (error) {
        console.warn("Failed to set RedactorX content:", error);
      }
    }

    if (editorEl?.current && disable) {
      const actualRedactorEditor = editorEl.current.parentElement
        ?.getElementsByClassName("rx-editor")
        .item(0);
      if (actualRedactorEditor) {
        actualRedactorEditor.setAttribute("contenteditable", "false");
        actualRedactorEditor.classList.add("bg-neutral-0");

        if (disable === true) {
          (actualRedactorEditor as HTMLElement).style.backgroundColor = "#F9F8FB";
        }

        const actualRedactorEditorText = actualRedactorEditor?.getElementsByTagName("p").item(0);
        if (actualRedactorEditorText) {
          actualRedactorEditorText.classList.add("select-none", "text-neutral-400");
        }
      }
    }
  }, [value, red, disable]);

  // TODO ref, not sure how to fix this one
  return <textarea ref={editorEl} name={name} id={id}></textarea>;
};

interface WYSIWYGEditorInputProps extends WYSIWYGEditorProps {
  field: string;
}

export const WYSIWYGEditorInput = ({ field, onChange, ...props }: WYSIWYGEditorInputProps) => {
  const { input } = useField(field);
  return (
    <WYSIWYGEditor
      defaultValue={input.value}
      {...props}
      onChange={(contents) => {
        input.onChange(contents);
        if (onChange) onChange(contents);
      }}
    />
  );
};

export default WYSIWYGEditorInput;
